import { FC, useCallback, useMemo, useRef } from "react";

import {
  AUCTION_CASH_CHEQUE_MINIMUM_AMOUNT,
  browserDefaultTimezone,
  DEFAULT_CHEQUE_MINIMUM_AMOUNT,
  FormType,
  formatSweepstakesEntryLevels,
  getField,
  getIsoLocaleFromCountry,
  getSweepstakesCountryName,
  getSweepstakesEligibleTerritory,
  getSweepstakesFreeEntryCount,
  getSweepstakesSponsorEntityType,
  getSweepstakesSponsorTaxIdLabel,
  getTicketingLink,
  getTicketRates,
  SweepstakesRulesInput,
  TicketingFormCategory,
} from "@simplyk/common";
import { useForm } from "react-hook-form";

import { trpc } from "../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { FormStepContainer } from "../FormStepContainer";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { AdvancedOptions } from "./AdvancedOptions/AdvancedOptions";
import { joinNotificationEmails, parseNotificationEmails } from "./helpers";
import { StepAdvancedSettingsFormData } from "./types";
import { SweepstakesRulesPreview } from "./SweepstakesRulesPreview";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { getSweepstakesRulesDates, isSweepstakesCategory } from "@/common/helpers/sweepstakes";

export const TicketingLiveEditorStepAdvancedSettings: FC = () => {
  const { t } = useTranslate();
  const utils = trpc.useUtils();
  const { organization, organizationCurrency } = useCurrentUserContext();

  const { apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();

  const organizationId = apiTicketing.organizationId ?? "";
  const { data: organizationPageInfo, isLoading: isOrganizationPageInfoLoading } =
    trpc.getOrganizationPageInfo.useQuery(
      { organizationId },
      { enabled: Boolean(organizationId), refetchOnMount: "always" }
    );

  const hasAnyOccurrencesAndHasAnOrgPage = Boolean(
    apiTicketing?.occurrences?.length && Boolean(organizationPageInfo?.isPublished)
  );
  const hasNotAnyOccurrencesAndHasAnOrgPage = Boolean(
    !apiTicketing?.occurrences?.length && Boolean(organizationPageInfo?.isPublished)
  );

  const ticketingField = getField(apiTicketing.ticketingFields, apiTicketing.locale);
  const sweepstakesRulesInput = useMemo<SweepstakesRulesInput>(() => {
    const sponsorFullAddress =
      organization &&
      [
        organization.address,
        organization.city,
        organization.region,
        organization.postalCode,
        getSweepstakesCountryName(organization.country),
      ]
        .filter(Boolean)
        .join(", ");
    const activeRates = (apiTicketing.rates ?? []).filter((rate) => !("isArchived" in rate && rate.isArchived));
    const entryLevelsTable = formatSweepstakesEntryLevels(
      getTicketRates(activeRates)
        .filter((rate) => !rate.isHidden && rate.amount !== null && rate.amount !== undefined)
        .map((rate) => ({
          title: getField(rate.rateFields, apiTicketing.locale)?.title,
          entryCount: rate.attendeesByTicket,
          formattedAmount: new Intl.NumberFormat(apiTicketing.locale, {
            style: "currency",
            currency: organizationCurrency,
          }).format((rate.amount ?? 0) / 100),
        }))
    );
    const rulesTimezone = apiTicketing.eventTimezone || organization?.timezone || browserDefaultTimezone;
    const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_URL;
    const campaignUrl =
      frontendUrl && organization
        ? `${frontendUrl.replace(/\/$/, "")}${getTicketingLink(
            apiTicketing.path,
            getIsoLocaleFromCountry(organization.country, apiTicketing.locale)
          )}`
        : null;

    return {
      country: organization?.country,
      sponsorLegalName: organization?.legalName || organization?.name,
      sponsorEntityType: getSweepstakesSponsorEntityType(organization?.country, organization?.type),
      sponsorTaxIdLabel: getSweepstakesSponsorTaxIdLabel(organization?.country, organization?.type),
      sponsorTaxId: organization?.charityNumber,
      sponsorFullAddress,
      sponsorContactPhone: organization?.phoneNumber,
      eligibleTerritory: getSweepstakesEligibleTerritory(organization?.country),
      ...getSweepstakesRulesDates(apiTicketing, rulesTimezone),
      campaignUrl,
      entryLevelsTable,
      sponsorContactEmail: organization?.ownerEmail,
      freeEntriesPerRequest: getSweepstakesFreeEntryCount(activeRates),
      zeffyPrivacyPolicyUrl: "https://www.zeffy.com/en-US/privacy-policy",
      sweepstakesName: ticketingField?.title,
    };
  }, [apiTicketing, organization, organizationCurrency, ticketingField?.title]);

  const { data: formFundsResult } = trpc.getFormFunds.useQuery(
    {
      formId: apiTicketing.id,
      formType: FormType.Ticketing,
      locale: apiTicketing.locale,
    },
    { enabled: Boolean(apiTicketing.id) }
  );

  const isSupportFormOnOrgPage = organizationPageInfo?.supportForms?.some((sf) => sf.id === apiTicketing.id);
  const supportFormsCount = organizationPageInfo?.supportForms?.length ?? 0;
  const hasRoomOnOrgPage = hasNotAnyOccurrencesAndHasAnOrgPage && supportFormsCount < 3;

  const formValues = useMemo<StepAdvancedSettingsFormData>(() => {
    const hasSingleLinkedFund = formFundsResult?.funds.length === 1;
    const hiddenFromOrgPageAtUtc = apiTicketing.hiddenFromOrganizationPageAtUtc;
    const displayOnOrgPage = hasAnyOccurrencesAndHasAnOrgPage ? !hiddenFromOrgPageAtUtc : undefined;
    const displayOnOrgPageSupportForm = hasNotAnyOccurrencesAndHasAnOrgPage
      ? hasRoomOnOrgPage || (isSupportFormOnOrgPage ?? false)
      : undefined;

    const salesOpenDate = apiTicketing.salesOpenDate ? new Date(apiTicketing.salesOpenDate) : null;
    const salesClosedDate = apiTicketing.salesClosedDate ? new Date(apiTicketing.salesClosedDate) : null;

    return {
      displayThermometer: apiTicketing.thermometerTarget !== null,
      thermometerTarget: apiTicketing.thermometerTarget || undefined,
      chequeEnabled: apiTicketing.chequeMinimumAmount !== null,
      chequeMinimumAmount: apiTicketing.chequeMinimumAmount ?? undefined,
      chequeDescription: ticketingField?.chequeDescription || "",
      allowInHonour: false,
      displayBidHistory: apiTicketing.displayBidHistory ?? true,
      fundDesignationEnabled: hasSingleLinkedFund,
      selectedFundId: hasSingleLinkedFund ? formFundsResult!.funds[0].fundId : null,
      displayOnOrgPage,
      displayOnOrgPageSupportForm,
      timezone: apiTicketing.eventTimezone || browserDefaultTimezone,
      hasSalesOpenAndCloseDate: Boolean(salesOpenDate || salesClosedDate),
      salesOpenDate,
      salesClosedDate,
      notificationEmails: joinNotificationEmails(apiTicketing.notificationEmails),
    };
  }, [
    apiTicketing,
    ticketingField?.chequeDescription,
    formFundsResult,
    hasAnyOccurrencesAndHasAnOrgPage,
    hasNotAnyOccurrencesAndHasAnOrgPage,
    hasRoomOnOrgPage,
    isSupportFormOnOrgPage,
  ]);

  const form = useForm<StepAdvancedSettingsFormData>({
    values: formValues,
    resetOptions: { keepDirtyValues: true },
  });

  const previousChequeEnabled = useRef(formValues.chequeEnabled);
  const lastChangedField = useRef<string | undefined>(undefined);
  const getScrollContext = useCallback(() => {
    const chequeEnabled = form.getValues("chequeEnabled");
    if (chequeEnabled !== previousChequeEnabled.current) {
      lastChangedField.current = "chequeEnabled";
      previousChequeEnabled.current = chequeEnabled;
    }
    return { targetElementId: lastChangedField.current === "chequeEnabled" ? "payment-method-cheque" : undefined };
  }, [form]);

  const shouldAlwaysSaveForOrgPageInit = Boolean(hasRoomOnOrgPage && !isSupportFormOnOrgPage);

  const editAdvancedParametersMutation = trpc.updateTicketingAdvancedSettings.useMutation();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepAdvancedSettingsFormData>({
    type: "ticketing",
    form,
    alwaysSaveOnNext: shouldAlwaysSaveForOrgPageInit,
    alwaysSaveOnBack: shouldAlwaysSaveForOrgPageInit,
    saveData: async (liveDataToMutate) => {
      const fundFieldsTouched =
        form.formState.dirtyFields.fundDesignationEnabled || form.formState.dirtyFields.selectedFundId;

      const isAuction = apiTicketing.formCategory === TicketingFormCategory.Auction;

      const result = await editAdvancedParametersMutation.mutateAsync({
        ticketingId: apiTicketing.id,
        chequeMinimumAmount: liveDataToMutate.chequeEnabled
          ? isAuction
            ? AUCTION_CASH_CHEQUE_MINIMUM_AMOUNT
            : (liveDataToMutate.chequeMinimumAmount ?? DEFAULT_CHEQUE_MINIMUM_AMOUNT)
          : null,
        thermometerTarget:
          liveDataToMutate.displayThermometer && liveDataToMutate.thermometerTarget
            ? liveDataToMutate.thermometerTarget
            : null,
        ...(form.formState.dirtyFields.notificationEmails && {
          notificationEmails: parseNotificationEmails(liveDataToMutate.notificationEmails),
        }),
        chequeDescription: liveDataToMutate.chequeEnabled ? liveDataToMutate.chequeDescription : null,
        offlinePaymentWording: liveDataToMutate.chequeDescription,
        selectedFundId: fundFieldsTouched
          ? liveDataToMutate.fundDesignationEnabled
            ? liveDataToMutate.selectedFundId
            : null
          : undefined,
        ...(liveDataToMutate.displayOnOrgPage !== undefined && {
          hiddenFromOrgPageEvents: !liveDataToMutate.displayOnOrgPage,
        }),
        displayBidHistory: liveDataToMutate.displayBidHistory,
        ...(liveDataToMutate.displayOnOrgPageSupportForm !== undefined && {
          supportFormOnOrgPage: {
            isActive: liveDataToMutate.displayOnOrgPageSupportForm,
            ...(liveDataToMutate.replaceFormOnOrgPage && {
              replaceForm: liveDataToMutate.replaceFormOnOrgPage,
            }),
          },
        }),
        eventTimezone: liveDataToMutate.timezone,
        salesOpenDate: liveDataToMutate.hasSalesOpenAndCloseDate ? (liveDataToMutate.salesOpenDate ?? null) : null,
        salesClosedDate: liveDataToMutate.hasSalesOpenAndCloseDate ? (liveDataToMutate.salesClosedDate ?? null) : null,
      });

      if (fundFieldsTouched) {
        void utils.getFormFunds.invalidate({
          formId: apiTicketing.id,
          formType: FormType.Ticketing,
          locale: apiTicketing.locale,
        });
      }
      if (hasNotAnyOccurrencesAndHasAnOrgPage && form.formState.dirtyFields.displayOnOrgPageSupportForm) {
        void utils.getOrganizationPageInfo.invalidate({
          organizationId: apiTicketing.organizationId ?? "",
        });
      }
      return result;
    },
  });

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, draft) => {
      getScrollContext();
      draft.lastChangedAdvancedField = lastChangedField.current;
      draft.ticketing.chequeMinimumAmount = formData.chequeEnabled
        ? apiTicketing.formCategory === TicketingFormCategory.Auction
          ? AUCTION_CASH_CHEQUE_MINIMUM_AMOUNT
          : (formData.chequeMinimumAmount ?? DEFAULT_CHEQUE_MINIMUM_AMOUNT)
        : null;
      draft.ticketing.ticketingFields = draft.ticketing.ticketingFields?.map((field) => ({
        ...field,
        chequeDescription: formData.chequeDescription,
      }));
      if (formData.displayThermometer && formData.thermometerTarget) {
        draft.ticketing.thermometerTarget = formData.thermometerTarget;
      } else {
        draft.ticketing.thermometerTarget = null;
      }
      if (hasAnyOccurrencesAndHasAnOrgPage && formData.displayOnOrgPage !== undefined) {
        draft.ticketing.hiddenFromOrganizationPageAtUtc = formData.displayOnOrgPage ? null : new Date().toISOString();
      }
      const salesOpenDate = formData.hasSalesOpenAndCloseDate ? (formData.salesOpenDate ?? null) : null;
      const salesClosedDate = formData.hasSalesOpenAndCloseDate ? (formData.salesClosedDate ?? null) : null;
      draft.ticketing.salesOpenDate = salesOpenDate ? new Date(salesOpenDate).toISOString() : null;
      draft.ticketing.salesClosedDate = salesClosedDate ? new Date(salesClosedDate).toISOString() : null;
    },
  });

  return (
    <FormStepContainer
      title={t("dashboard", "step.advancedSettings.title")}
      form={form}
      getScrollContext={getScrollContext}
      actionsProps={{
        onNext: handleSaveAndNext,
        onBack: handleSaveAndBack,
      }}
    >
      <AdvancedOptions
        form={form}
        formType={FormType.Ticketing}
        formId={apiTicketing.id}
        locale={apiTicketing.locale}
        organizationPageInfo={organizationPageInfo}
        isOrganizationPageInfoLoading={isOrganizationPageInfoLoading}
        hasAnyOccurrencesAndHasAnOrgPage={hasAnyOccurrencesAndHasAnOrgPage}
        hasNotAnyOccurrencesAndHasAnOrgPage={hasNotAnyOccurrencesAndHasAnOrgPage}
        hasNoOccurrences={!apiTicketing?.occurrences?.length}
        ticketingTimezone={apiTicketing.eventTimezone || browserDefaultTimezone}
        afterDisplaySection={
          isSweepstakesCategory(apiTicketing.formCategory) && <SweepstakesRulesPreview input={sweepstakesRulesInput} />
        }
      />
    </FormStepContainer>
  );
};
